import React, { useState, useEffect } from 'react';
import API from '../utils/const';
import LoadingOverlay from './LoadingOverlay';
import { createToast } from './Toast';
import { MessageKind } from '../utils/common';
import { useTranslation } from 'react-i18next';
import { CalendarProvider, convertToAdminConsentUrl } from '../utils/common';
import { GoogleSVG, ICSLogoSVG, OutlookSVG, AppleSVG } from './SVG';
import { Modal, TextInput, Button, Stack, Pill, Group, Text, Flex, Badge, Input, ActionIcon } from '@mantine/core';
import { IconClipboard } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { handleApiError } from '../utils/app';
import { useAuth } from '../contexts/AuthContext';
import { Box } from '@mantine/core';
import { User, authFetch } from '../utils/session';
import { Account } from './AccountCard';
import { MagicLinkConnect } from './MagicLinkConnect';
import { trackCalendarConnectClick, trackCalendarConnected } from '../utils/analytics';

export const AddCalendarAccount: React.FC<{ isConnected: boolean, glowing: boolean, provider: CalendarProvider, withTos?: boolean, onSuccess?: () => void, isBeta?: boolean }> = ({ isConnected, glowing, provider, withTos = true, onSuccess = () => { }, isBeta = false }) => {
    const { t } = useTranslation(['app']);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isOutlookModalOpen, setIsOutlookModalOpen] = useState<boolean>(false);
    const [isAppleModalOpen, setIsAppleModalOpen] = useState<boolean>(false);
    const [adminApprovalUrl, setAdminApprovalUrl] = useState<string>('');
    const [normalOAuthUrl, setNormalOAuthUrl] = useState<string>('');
    const { isAuthenticated } = useAuth();

    const notifySuccess = () => {
        trackCalendarConnected(provider);
        onSuccess();
    };

    const form = useForm({
        initialValues: {
            url: '',
            name: ''
        },
        validate: {
            url: (value) => {
                if (!value) return t('dashboard.required-field');
                if (!value.startsWith('http') && !value.startsWith('webcal')) return t('common.invalid-url');
                return null;
            },
            name: (value) => {
                if (!value) return t('dashboard.required-field');
                return null;
            }
        }
    });

    const appleForm = useForm({
        initialValues: {
            email: '',
            password: ''
        },
        validate: {
            email: (value) => {
                if (!value) return t('dashboard.required-field');
                if (!value.includes('@')) return t('common.invalid-email');
                return null;
            },
            password: (value) => {
                if (!value) return t('dashboard.required-field');
                return null;
            }
        }
    });

    const addAccount = async () => {
        trackCalendarConnectClick(provider);
        if (provider === CalendarProvider.ICS) {
            if (!isAuthenticated) {
                window.location.href = `/login?signup=true`;
                return;
            }
            setIsModalOpen(true);
            return;
        }

        if (provider === CalendarProvider.APPLE) {
            if (!isAuthenticated) {
                window.location.href = `/login?signup=true`;
                return;
            }
            setIsAppleModalOpen(true);
            return;
        }

        if (provider === CalendarProvider.OUTLOOK) {
            setIsOutlookModalOpen(true);
            return;
        }

        try {
            setIsLoading(true);

            // get utm campaign from url
            let utmCampaign = null;
            try {
                const urlParams = new URLSearchParams(window.location.search);
                utmCampaign = urlParams.get('utm_campaign');
            } catch (error) {
                console.error('Error getting utm campaign:', error);
            }


            const params = new URLSearchParams();
            if (utmCampaign) {
                params.set('utm_campaign', utmCampaign);
            }
            const query = params.toString();
            const response = await authFetch(
                `${API}/${provider}/calendar/prepare${query ? `?${query}` : ''}`,
                { method: 'GET' }
            );

            if (!response.ok) {
                createToast(t('dashboard.add-calendar.add-account-error'), MessageKind.Error);
                return;
            }
            const data = await response.json();
            window.location.href = data.url;
        } catch (error) {
            console.error('Error fetching calendars:', error);
            createToast(t('dashboard.add-calendar.add-account-error'), MessageKind.Error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const generateAdminApprovalUrl = async () => {
            if (isOutlookModalOpen && !adminApprovalUrl) {
                try {
                    let utmCampaign = null;
                    try {
                        const urlParams = new URLSearchParams(window.location.search);
                        utmCampaign = urlParams.get('utm_campaign');
                    } catch (error) {
                        console.error('Error getting utm campaign:', error);
                    }

                    const params = new URLSearchParams();
                    if (utmCampaign) {
                        params.set('utm_campaign', utmCampaign);
                    }
                    const query = params.toString();
                    const response = await authFetch(
                        `${API}/${CalendarProvider.OUTLOOK}/calendar/prepare${query ? `?${query}` : ''}`,
                        { method: 'GET' }
                    );

                    if (!response.ok) {
                        return;
                    }
                    const data = await response.json();
                    setNormalOAuthUrl(data.url);
                    setAdminApprovalUrl(convertToAdminConsentUrl(data.url));
                } catch (error) {
                    console.error('Error generating admin approval URL:', error);
                }
            }
        };

        generateAdminApprovalUrl();
    }, [isOutlookModalOpen, adminApprovalUrl]);

    const handleOutlookNormalFlow = async () => {
        if (normalOAuthUrl) {
            window.location.href = normalOAuthUrl;
        }
    }

    const handleOutlookEnterpriseFlow = async () => {
        if (normalOAuthUrl) {
            // Change the prompt parameter to select_account instead of consent
            if (normalOAuthUrl) {
                try {
                    const url = new URL(normalOAuthUrl);
                    url.searchParams.set('prompt', 'select_account');
                    window.location.href = url.toString();
                } catch (e) {
                    // Fallback to direct redirect if URL parsing fails
                    window.location.href = normalOAuthUrl;
                }
            }
        }
    }

    const copyAdminApprovalUrl = () => {
        navigator.clipboard.writeText(adminApprovalUrl);
        createToast(t('dashboard.link_copied'), MessageKind.Success);
    };

    const handleICSSubmit = async (values: { url: string, name: string }) => {
        try {
            setIsLoading(true);
            const response = await authFetch(
                `${API}/accounts/ics`,
                {
                    method: 'POST',
                    body: JSON.stringify({ url: values.url, name: values.name }),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.ok) {
                createToast(t('dashboard.calendar.added-message'), MessageKind.Success);
                setIsModalOpen(false);
                form.reset();
                notifySuccess();
            } else {
                handleApiError(response, t, true);
            }
        } catch (error) {
            console.error('Error adding ICS calendar:', error);
            handleApiError(error, t, true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAppleSubmit = async (values: { email: string, password: string }) => {
        try {
            setIsLoading(true);
            const response = await authFetch(
                `${API}/accounts/apple`,
                {
                    method: 'POST',
                    body: JSON.stringify({ email: values.email, password: values.password }),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.ok) {
                createToast(t('dashboard.calendar.added-message'), MessageKind.Success);
                setIsAppleModalOpen(false);
                appleForm.reset();
                notifySuccess();
            } else {
                handleApiError(response, t, true);
            }
        } catch (error) {
            console.error('Error adding Apple calendar:', error);
            handleApiError(error, t, true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Modal
                opened={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                withCloseButton={false}
                centered
            >
                <form onSubmit={form.onSubmit(handleICSSubmit)}>
                    <Stack>
                        <TextInput
                            required
                            label={t('dashboard.add-calendar.ics-url-label')}
                            placeholder={t('dashboard.add-calendar.ics-url-placeholder')}
                            {...form.getInputProps('url')}
                        />
                        <TextInput
                            required
                            label={t('input.calendar-name')}
                            placeholder={t('input.calendar-name-placeholder')}
                            {...form.getInputProps('name')}
                        />
                        <Button type="submit" loading={isLoading} fullWidth>
                            {t('common.save')}
                        </Button>
                    </Stack>
                </form>
            </Modal>
            <Modal
                opened={isOutlookModalOpen}
                onClose={() => {
                    setIsOutlookModalOpen(false);
                    setAdminApprovalUrl('');
                    setNormalOAuthUrl('');
                }}
                withCloseButton={true}
                centered
                title={t('dashboard.add-calendar.outlook-modal-title')}
            >
                <Stack gap="md">
                    <Text>
                        {t('dashboard.add-calendar.outlook-admin-intro')}
                    </Text>
                    <Button
                        onClick={handleOutlookNormalFlow}
                        loading={!normalOAuthUrl}
                        fullWidth
                    >
                        {t('dashboard.add-calendar.outlook-try-link')}
                    </Button>
                    {adminApprovalUrl && (
                        <>
                            <Text>{t('dashboard.add-calendar.outlook-admin-link-hint')}</Text>
                            <Input
                                value={adminApprovalUrl}
                                onChange={() => { }}
                                rightSection={
                                    <ActionIcon variant="light" aria-label={t('dashboard.add-calendar.copy-aria-label')} onClick={copyAdminApprovalUrl}>
                                        <IconClipboard style={{ width: '70%', height: '70%' }} stroke={1.5} />
                                    </ActionIcon>
                                }
                                rightSectionPointerEvents="all"
                                readOnly
                            />
                        </>
                    )}

                    <Text>{t('dashboard.add-calendar.outlook-after-approval')}</Text>
                    <Button
                        onClick={handleOutlookEnterpriseFlow}
                        loading={!adminApprovalUrl}
                        fullWidth
                        variant="light"
                    >
                        {t('dashboard.add-calendar.outlook-after-approval-button')}
                    </Button>
                </Stack>
            </Modal>
            <Modal
                opened={isAppleModalOpen}
                onClose={() => setIsAppleModalOpen(false)}
                withCloseButton={false}
                centered
                title={t('dashboard.add-calendar.apple-modal-title')}
            >
                <form onSubmit={appleForm.onSubmit(handleAppleSubmit)}>
                    <Stack>
                        <Stack gap="xs">
                            <Text size="sm" c="dimmed">
                                {t('dashboard.add-calendar.apple-password-intro-before-link')}{' '}
                                <a href="https://account.apple.com/account/manage" target="_blank" rel="noopener noreferrer">
                                    https://account.apple.com/account/manage
                                </a>
                                {' '}
                                {t('dashboard.add-calendar.apple-password-intro-after-link')}
                            </Text>
                            <Text size="sm" c="dimmed">
                                {t('dashboard.add-calendar.apple-onboarding-video')}{' '}
                                <a href="https://youtu.be/90SSUGOBQdo" target="_blank" rel="noopener noreferrer">
                                    {t('dashboard.add-calendar.apple-youtube-link-label')}
                                </a>
                            </Text>
                        </Stack>
                        <TextInput
                            required
                            label={t('dashboard.add-calendar.apple-calendar-email-label')}
                            placeholder={t('dashboard.add-calendar.apple-calendar-email-placeholder')}
                            type="email"
                            {...appleForm.getInputProps('email')}
                        />
                        <TextInput
                            required
                            label={t('dashboard.add-calendar.apple-app-password-label')}
                            placeholder={t('dashboard.add-calendar.apple-app-password-placeholder')}
                            type="password"
                            {...appleForm.getInputProps('password')}
                        />
                        <Button type="submit" loading={isLoading} fullWidth>
                            {t('common.save')}
                        </Button>
                    </Stack>
                </form>
            </Modal>
            {isLoading && <LoadingOverlay />}
            <Button c='black' bg='gray.0' variant='outline' className={`${glowing ? 'glowing' : ''}`} onClick={addAccount} px={8} w="300px">
                <Group gap='xs'>
                    <Box w={22} h={22} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                        {provider === CalendarProvider.GOOGLE &&
                            <GoogleSVG width={22} height={22} />
                        }
                        {provider === CalendarProvider.OUTLOOK &&
                            <OutlookSVG width={22} height={22} />
                        }
                        {provider === CalendarProvider.ICS &&
                            <ICSLogoSVG width={22} height={22} />
                        }
                        {provider === CalendarProvider.APPLE &&
                            <AppleSVG width={22} height={22} />
                        }
                    </Box>
                    <Text fz={{ base: 'xs', sm: 'sm' }}>{t(`dashboard.connect-${provider}`)}</Text>
                    {isBeta &&
                        <Badge
                            variant='light'
                            c="brand.7"
                            ml='md'
                            size='xs'
                        > {t('dashboard.add-calendar.beta-badge')} </Badge>
                    }
                </Group>
            </Button>
            {!isConnected && withTos &&
                <div className='mx-auto mt-1 small' dangerouslySetInnerHTML={{ __html: t("common.accept-tos") }}>
                </div>
            }
        </>

    );
};

export const AddCalendarAccountWrapper: React.FC<{ user: User, accounts: Account[], updateAccounts: () => void }> = ({ user, accounts, updateAccounts }) => {
    const { isAuthenticated } = useAuth();
    return <>
        <Flex
            align="center"
            direction="column"
            w="300px"
            maw="300px"
            justify="center"
            mx={"auto"}
            mb={"lg"}
        >
            <Stack>
                <AddCalendarAccount provider={CalendarProvider.GOOGLE} isConnected={user != null} glowing={accounts != null && accounts.length < 2} withTos={false} />
                <AddCalendarAccount provider={CalendarProvider.OUTLOOK} isConnected={user != null} glowing={accounts != null && accounts.length < 2} withTos={false} isBeta={false} />
                {isAuthenticated &&
                    < AddCalendarAccount provider={CalendarProvider.ICS} isConnected={user != null} glowing={accounts != null && accounts.length < 2} withTos={true} onSuccess={() => { updateAccounts(); }} isBeta={false} />
                }
                {isAuthenticated &&
                    < AddCalendarAccount provider={CalendarProvider.APPLE} isConnected={user != null} glowing={accounts != null && accounts.length < 2} withTos={true} onSuccess={() => { updateAccounts(); }} isBeta={false} />
                }
            </Stack>
            {isAuthenticated && <MagicLinkConnect />}
        </Flex>
    </>
}

export default AddCalendarAccount;